import Big from 'big.js';
import { mapGetters, mapActions, mapState } from 'vuex';

import { funcoes, SituacaoEnum } from '@/utils/enumerados';

import {
  formatarValor,
  formatarPorcentagem,
  formatarNumero,
  formatarPadrao,
  formatType,
  tituloEstadoItem,
  estadoItem,
} from '@/utils/format';

export default {
  data() {
    const loading = {
      confirm: false,
      documents: false,
      general: false,
    };

    return {
      loading,
    };
  },
  filters: {
    formatarValor,
    formatarNumero,
    formatType,
    tituloEstadoItem,
    estadoItem,
    formatarPadrao,
  },
  computed: {
    ...mapState('companies', ['company']),
    ...mapGetters('proposals', { proposals: 'obterPropostasUsuarioLogado' }),
    ...mapState('auctionsBidders', ['userLoggedBidder']),
    ...mapGetters({
      auction: 'obterOfertaCompra',
      function: 'getFunction',
      deadlines: 'obterOfertaCompraPrazos',
      isSineDieFromEntregaDePropostas: 'isSineDieFromEntregaDePropostas',
    }),
    currentQualificationDeadline() {
      return this.deadlines.find(prazo => Number(prazo.prazo_id) === 11 && !prazo.data_ate);
    },
    latestClosedAdhesionDeadline() {
      const closedAdhesionDeadlines = this.deadlines
        .filter(prazo => Number(prazo.prazo_id) === 13 && prazo.data_ate)
        .sort((a, b) => new Date(b.data_ate).getTime() - new Date(a.data_ate).getTime());

      return closedAdhesionDeadlines[0] || null;
    },
    isAdhesionQualificationContext() {
      const deadline = this.currentQualificationDeadline;
      const contextFromField = (
        deadline?.qualification_context ||
        deadline?.qualificationContext ||
        ''
      )
        .toString()
        .toUpperCase();
      if (contextFromField === 'ADESAO') return true;
      if (contextFromField === 'NORMAL') return false;

      const context = deadline?.observacao;
      if (typeof context === 'string') {
        const normalizedContext = context.toUpperCase();
        if (normalizedContext.includes('QUALIFICATION_CONTEXT:ADESAO')) {
          return true;
        }
        if (normalizedContext.includes('QUALIFICATION_CONTEXT:NORMAL')) {
          return false;
        }
      }

      if (
        typeof deadline?.nome === 'string' &&
        /habilita[cç][aã]o da ades[aã]o/i.test(deadline.nome)
      ) {
        return true;
      }

      const qualificationStart = new Date(deadline?.data_de).getTime();
      const adhesionEnd = new Date(this.latestClosedAdhesionDeadline?.data_ate).getTime();
      const ONE_SECOND_MS = 1000;

      if (!Number.isFinite(qualificationStart) || !Number.isFinite(adhesionEnd)) {
        return false;
      }

      return Math.abs(qualificationStart - adhesionEnd) <= ONE_SECOND_MS;
    },
    adherenceStep() {
      if (this.auctionSituation === SituacaoEnum.ADESAO_DE_PRECO) {
        return true;
      }

      if (this.auctionSituation === SituacaoEnum.HABILITACAO) {
        return this.isAdhesionQualificationContext;
      }

      return false;
    },
    authorizedBuyer() {
      return this.crier || this.authority;
    },
    bidStarted() {
      // não considerar a situação CADASTRADA, pois o fluxo normal não aceita.
      return this.items.some(
        item => ['LANCE', 'LANCE-RANDOMICO', 'LANCE-FECHADO'].indexOf(item.estado) > -1,
      );
    },
    bidsStep() {
      return this.deadlines.some(prazo => prazo.prazo_id === 8);
    },
    biggestDiscount() {
      return this.negotiationType === 3;
    },
    buyer() {
      return this.company.buyer && this.auction.comprador.id === this.company.id;
    },
    discloseReferential() {
      return this.auction.divulgar_referencial;
    },
    displayCompany() {
      return (
        (this.auctionSituation === 17 && this.enablingDeadline) ||
        [
          SituacaoEnum.HABILITACAO,
          SituacaoEnum.INTERPOSICAO_DE_RECURSOS,
          SituacaoEnum.MANIFESTACAO_INTENCAO_RECURSO,
          SituacaoEnum.ADESAO_DE_PRECO,
          SituacaoEnum.ENCERRADA_COM_VENCEDOR,
          SituacaoEnum.ENCERRADA_COM_RECURSO,
          SituacaoEnum.FRACASSADA,
        ].indexOf(this.auctionSituation) > -1
      );
    },
    displayProviderTag() {
      return (
        [
          SituacaoEnum.LANCES,
          SituacaoEnum.PREFERENCIA,
          SituacaoEnum.NEGOCIACAO,
          SituacaoEnum.JULGAMENTO,
          SituacaoEnum.INTERPOSICAO_DE_RECURSOS,
          SituacaoEnum.MANIFESTACAO_INTENCAO_RECURSO,
        ].indexOf(this.auctionSituation) > -1 ||
        (this.auctionSituation === SituacaoEnum.SUSPENSA && !this.enablingDeadline)
      );
    },
    displayFooter() {
      return (
        (this.bidder && this.auctionSituation === 6 && this.bidStarted) ||
        (this.auctionSituation === 7 && this.preferenceFilter) ||
        (this.auctionSituation === 8 && this.negotiationFilter) ||
        (this.auctionSituation === 19 && this.negotiationFilter) ||
        (this.auctionSituation === 9 && this.enablingFilter)
      );
    },
    displayReductionColumn() {
      return [6, 13, 14, 15, 16, 17].indexOf(this.auctionSituation) > -1;
    },
    displayReferralColumn() {
      return (
        this.authorizedBuyer ||
        this.biggestDiscount ||
        (this.auctionSituation > 7 && this.negotiationDeadline && this.discloseReferential)
      );
    },
    displaySizeColumn() {
      return (
        [6, 7, 8, 19].indexOf(this.auctionSituation) > -1 ||
        (this.auctionSituation === 17 && !this.enablingDeadline)
      );
    },
    displayStatusColumn() {
      return (
        this.auctionSituation > SituacaoEnum.CADASTRADA &&
        (this.auctionSituation <= SituacaoEnum.JULGAMENTO ||
          this.auctionSituation === SituacaoEnum.MANIFESTACAO_INTENCAO_RECURSO)
      );
    },
    enablingDeadline() {
      return this.deadlines.some(prazo => prazo.prazo_id === 11);
    },
    enablingFilter() {
      return this.items.find(
        item =>
          item.estado === 'HABILITACAO' &&
          this.userLoggedBidder.alias === item.menor_lance.fornecedor,
      );
    },
    exclusiveItem() {
      return this.items.some(item => item.item.exclusivo);
    },
    exclusiveNotice() {
      return this.auction.tipo_edital === 'RESERVADO';
    },
    justify() {
      return this.windowWidth > 960;
    },
    mobile() {
      return this.windowWidth < 769;
    },
    negotiationFilter() {
      return this.items.some(
        item =>
          (item.estado === 'NEGOCIACAO' || item.estado === 'JULGAMENTO') &&
          this.userLoggedBidder.alias === item.menor_lance.fornecedor,
      );
    },
    negotiationDeadline() {
      return this.deadlines.some(prazo => prazo.prazo_id === 10);
    },
    negotiationType() {
      return this.auction.tipo_negociacao.id;
    },
    auctionSituation() {
      return this.auction.situacao.id;
    },
    preferenceFilter() {
      return this.items.some(
        item =>
          item.estado === 'PREFERENCIA-INICIADA' &&
          this.userLoggedBidder.alias === item.preferencia.fornecedor,
      );
    },
    preferenceStarted() {
      return this.items.some(item => item.estado === 'PREFERENCIA-INICIADA');
    },
    provider() {
      return this.function === funcoes.FORNECEDOR;
    },
    regionalPreferenceEnabled() {
      return this.exclusiveNotice && this.auction.preferencia_regional;
    },
    seller() {
      return this.function === funcoes.VENDEDOR;
    },
    crier() {
      return this.function === funcoes.PREGOEIRO;
    },
    authority() {
      return this.function === funcoes.AUTORIDADE;
    },
    bidder() {
      return this.function === funcoes.LICITANTE;
    },
    lowerBidColor() {
      return item => {
        if (!item.menor_lance.fornecedor || ['FRACASSADA', 'REVOGADO'].indexOf(item.estado) > -1) {
          return '';
        }

        if (this.buyer) {
          if (!item.referencia && !item.total) {
            if (this.biggestDiscount && item.menor_lance.percentual === 0) {
              return 'is-danger';
            }
            return 'is-success';
          }

          if (this.negotiationType === 1 && item.referencia >= item.menor_lance.valor) {
            return 'is-success';
          }
          if ([2, 3].indexOf(this.negotiationType) > -1 && item.total >= item.menor_lance.valor) {
            return 'is-success';
          }
          return 'is-danger';
        }

        if (this.bidder) {
          if (item.menor_lance.fornecedor === this.userLoggedBidder.alias) {
            return 'is-success';
          }
          return 'is-danger';
        }

        return 'is-dark';
      };
    },
    visitor() {
      return this.function === funcoes.VISITANTE;
    },
  },
  methods: {
    ...mapActions('auctionsDocuments', ['fetchAuctionBidderDocuments']),
    ...mapActions('requiredDocuments', ['fetchRequiredDocuments']),
    ...mapActions('itens', ['abdicarPreferencia', 'adesaoMenorPrecoItem']),
    bidderExists(item) {
      return item.menor_lance.fornecedor ? item.menor_lance.fornecedor : 'Nenhum fornecedor';
    },
    displayDocumentsList(row) {
      return (
        this.auctionSituation > 8 &&
        this.auctionSituation !== 13 &&
        this.enablingDeadline &&
        row.estado !== 'DESERTO' &&
        ((!this.biggestDiscount && row.menor_lance.valor !== 0) || this.biggestDiscount)
      );
    },
    displayEyeIcon(id) {
      const container = this.$refs[`description-container-${id}`];
      if (container) {
        return container.scrollWidth >= container.parentElement.scrollWidth;
      }

      return false;
    },
    displayOffersList(row) {
      return (
        this.auctionSituation > 4 &&
        (this.auctionSituation <= SituacaoEnum.JULGAMENTO ||
          this.auctionSituation === SituacaoEnum.MANIFESTACAO_INTENCAO_RECURSO) &&
        this.auctionSituation !== 13 &&
        row.estado !== 'DESERTO'
      );
    },
    displayPreference(preferenciaFornecedor) {
      return preferenciaFornecedor && [6, 7, 17].indexOf(this.auctionSituation) > -1;
    },
    formatDiscountPercentage(item) {
      return this.verifyZeroValue(item.menor_lance.percentual)
        ? `${formatarPorcentagem(item.menor_lance.percentual)}%`
        : 'Nenhum Valor';
    },
    formatMinorBid(row) {
      if (row.total) {
        return formatarValor(row.menor_lance.valor);
      }

      return formatarValor(0);
    },
    formatPercentageReduction(row) {
      return `${formatarPorcentagem(row.reducaoLancesPorcentagem || 0)}%`;
    },
    formatPreferenceBid(row) {
      if (row.total) {
        return formatarValor(row.preferencia.valor);
      }

      return formatarValor(0);
    },
    formatPreferenceDiscount(item) {
      return this.verifyZeroValue(item.preferencia.percentual) && item.estado !== 'REVOGADO'
        ? `${formatarPorcentagem(item.preferencia.percentual)}%`
        : '';
    },
    formatReference(item) {
      return (this.negotiationType === 1 ? item.referencia : item.total) || 0;
    },
    formatWinnerCompanySize(item) {
      if (item.menor_lance.fornecedor_porte === 'COOPERATIVA_PREFERENCIA') {
        return 'COOP. C/DIR. PREF.';
      }

      if (item.menor_lance.fornecedor_porte) {
        return item.menor_lance.fornecedor_porte;
      }
      return 'N/A';
    },
    minorBidExists(item) {
      return item.menor_lance.valor ? item.menor_lance.valor : 'Nenhum Lance';
    },
    openDescription(id) {
      const tag = this.$refs[`description-container-${id}`];
      if (tag) {
        const { classList } = tag;
        const contains = classList.contains('elipsis-column');
        if (contains) {
          classList.remove('elipsis-column');
          return;
        }
        classList.add('elipsis-column');
      }
    },
    preferenceBidExists(item) {
      return item.preferencia.fornecedor && item.estado !== 'REVOGADO'
        ? item.preferencia.fornecedor
        : '';
    },
    preferenceSizeExists(item) {
      if (!item.preferencia.fornecedor || item.estado === 'REVOGADO') return '';
      if (this.exclusiveNotice && this.auction.preferencia_regional) {
        return 'PREFERÊNCIA REGIONAL';
      }

      return 'PREFERÊNCIA';
    },
    preferenceValueExists(item) {
      return item.preferencia.valor && item.estado !== 'REVOGADO' ? item.preferencia.valor : '';
    },
    async sendDocuments(item) {
      // Adesão: licitante logado. NORMAL + inversão: licitante logado.
      // NORMAL sem inversão: menor lance (legado).
      const useLoggedBidder =
        this.adherenceStep || !!(this.bidder && this.auction?.phase_inversion);
      const bidderId = useLoggedBidder ? this.userLoggedBidder.id : item.menor_lance.fornecedor_id;
      await Promise.all([
        this.fetchRequiredDocuments(this.auction.id),
        this.fetchAuctionBidderDocuments({ auctionId: this.auction.id, bidderId, itemId: item.id }),
      ]);
      const company = {
        alias: this.userLoggedBidder.alias,
        bidderId,
        socialDenomination: this.company.socialDenomination,
      };

      this.$emitBus('documentos', { item, company });
    },
    verifyOverflowed() {
      const overflowed = this.items.some(item => this.displayEyeIcon(item.id));

      if (!overflowed) {
        const refs = Object.values(this.$refs);
        const rows = refs.filter(
          ref => ref && ref.parentNode && ref.parentNode.className === 'elipsis-size',
        );
        rows.forEach(row => row.parentNode.classList.remove('elipsis-size'));
      } else {
        this.descriptionKey += 1;
      }
    },
    verifyZeroValue(valor) {
      if ([null, undefined].indexOf(valor) === -1) {
        return new Big(valor).gte(0);
      }

      return false;
    },
    viewOffers(item) {
      this.$emitBus('detalhe', {
        tipo: 'oferta',
        item,
        ofertaCompraId: this.auction.id,
      });
    },
    viewBatchOffers(item) {
      this.$emitBus('detalhe', {
        tipo: 'oferta-lote',
        item,
        ofertaCompraId: this.auction.id,
      });
    },
    viewReadjustProposals(item, proposal) {
      this.$emitBus('detalhe', {
        tipo: 'readequacao-proposta',
        item,
        proposal,
        ofertaCompraId: this.auction.id,
      });
    },
  },
};
